import { useCallback, useEffect, useRef, useState } from 'react';

import { useEvent } from '@hooks/use-event';

type Message = (obj: { inputValue: string }) => string | null;

type LoadOptions<TOption> = (
  inputValue: string,
  callback: (options: TOption[]) => void,
) => void | Promise<TOption[]>;

type UseAsyncOptionsArgs<TOption> = {
  loadOptions: LoadOptions<TOption>;
  defaultOptions: boolean | TOption[] | undefined;
  isOpen: boolean;
  inputValue: string;
  noOptions: Message;
  loadingMessage: Message;
};

// loadOptions may hand results to the callback, return them as a promise, or
// both; a rejected promise counts as no results.
const resolveLoad = <TOption>({
  loadOptions,
  query,
  onResults,
}: {
  loadOptions: LoadOptions<TOption>;
  query: string;
  onResults: (results: TOption[]) => void;
}) => {
  const maybePromise = loadOptions(query, onResults);
  if (maybePromise && typeof (maybePromise as Promise<unknown>).then === 'function') {
    (maybePromise as Promise<TOption[]>).then(onResults, () => onResults([]));
  }
};

// The default options show whenever the query is empty; every non-empty query
// is a request, lists nothing until it answers, and only the latest one's
// results land.
export function useAsyncOptions<TOption>({
  loadOptions,
  defaultOptions,
  isOpen,
  inputValue,
  noOptions,
  loadingMessage,
}: UseAsyncOptionsArgs<TOption>) {
  const defaultItemsRef = useRef<TOption[]>(Array.isArray(defaultOptions) ? defaultOptions : []);
  const hasPendingDefaultsRef = useRef(defaultOptions === true);
  const [items, setItems] = useState<TOption[]>(defaultItemsRef.current);
  const [isLoading, setIsLoading] = useState(hasPendingDefaultsRef.current);
  const queryRef = useRef('');
  const lastRequestRef = useRef(0);
  const defaultsRequestedAtRef = useRef<number | null>(null);

  const load = useEvent(loadOptions);

  // With defaultOptions={true} the defaults are loadOptions('') results. A
  // debounced loadOptions drops that request when a query follows within its
  // delay, so once a query issued after it answers, stale or not, the request
  // counts as dropped and is asked again if the query is empty by then.
  const requestDefaults = useCallback(() => {
    if (!hasPendingDefaultsRef.current || defaultsRequestedAtRef.current !== null) return;
    defaultsRequestedAtRef.current = lastRequestRef.current;
    resolveLoad({
      loadOptions: load,
      query: '',
      onResults: (results) => {
        hasPendingDefaultsRef.current = false;
        defaultItemsRef.current = results;
        if (queryRef.current !== '') return;
        setIsLoading(false);
        setItems(results);
      },
    });
  }, [load]);

  const search = useCallback(
    (query: string) => {
      queryRef.current = query;
      const requestId = ++lastRequestRef.current;
      if (query === '') {
        setIsLoading(hasPendingDefaultsRef.current);
        setItems(defaultItemsRef.current);
        requestDefaults();
        return;
      }
      setIsLoading(true);
      setItems([]);
      resolveLoad({
        loadOptions: load,
        query,
        onResults: (results) => {
          const defaultsRequestedAt = defaultsRequestedAtRef.current;
          if (defaultsRequestedAt !== null && requestId > defaultsRequestedAt) {
            defaultsRequestedAtRef.current = null;
            if (queryRef.current === '') requestDefaults();
          }
          if (lastRequestRef.current !== requestId) return;
          setIsLoading(false);
          setItems(results);
        },
      });
    },
    [load, requestDefaults],
  );

  useEffect(() => {
    requestDefaults();
  }, [requestDefaults]);

  // Consumers fetch their default options after mount and pass them a render
  // later; show them unless a search is underway.
  const prevDefaultOptionsRef = useRef(defaultOptions);
  useEffect(() => {
    if (prevDefaultOptionsRef.current === defaultOptions) return;
    prevDefaultOptionsRef.current = defaultOptions;
    if (!Array.isArray(defaultOptions)) return;
    defaultItemsRef.current = defaultOptions;
    if (queryRef.current === '') search('');
  }, [defaultOptions, search]);

  // A closed list starts over from the default options on its next open, the
  // way the input text starts over from the committed selection.
  const wasOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (wasOpen && !isOpen) search('');
  }, [isOpen, search]);

  const emptyMessage = isLoading ? loadingMessage({ inputValue }) : noOptions({ inputValue });

  return { items, isLoading, emptyMessage, search };
}
